import { useQuery, type QueryClient } from '@tanstack/react-query'
import { api, hasAuth, clearAuth, ApiError, type AuthUser } from './api'
import { keepDevicePrefsOnly } from './preferences'
import { clearRecentCompanies } from './recent'

export async function resetUserState(queryClient: QueryClient) {
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' })
  await queryClient.invalidateQueries({ queryKey: ['me'] })
}

export async function signOut(queryClient: QueryClient) {
  clearAuth()
  clearRecentCompanies()
  keepDevicePrefsOnly()
  queryClient.setQueryData(['me'], null)
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' })
}

/** Sækir núverandi innskráðan notanda (eða null ef ekki innskráður). */
export function useCurrentUser() {
  return useQuery<AuthUser | null>({
    queryKey: ['me'],
    queryFn: async () => {
      if (!hasAuth()) return null
      try {
        const res = await api.me()
        return res.user
      } catch (err) {
        // An expired/invalid token still sits in localStorage, so hasAuth()
        // stays true and the UI would strand the user on the "Þú ert
        // innskráð(ur)" login screen with no way forward but a hard reload.
        // Clear it on 401 so they fall back to the login form cleanly.
        if (err instanceof ApiError && err.status === 401) {
          clearAuth()
        }
        return null
      }
    },
    staleTime: 60_000,
  })
}

export function isAdminRole(user: AuthUser | null | undefined): boolean {
  return user?.role === 'admin' || user?.role === 'superadmin'
}

export function isSuperAdminRole(user: AuthUser | null | undefined): boolean {
  return user?.role === 'superadmin'
}
